import React, { useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';

import WinaLoader from '../../../../components/WinaLoader';
import { PromotionStatuses, type PromotionStatus } from '../../../../types/promotions';
import { MissionCardComplexeStatuses, type MissionCardComplexeStatus } from '../../types/components';
import { type MissionOperationType } from '../../types/state/missions';
import MissionCardCTA from '../MissionCardCTA';
import MissionDetailsAchievementsListContainer from '../MissionDetailsAchievementsList/MissionDetailsAchievementsListContainer';
import MissionDetailsBetRedirectionButtonContainer from '../MissionDetailsBetRedirectionButton/MissionDetailsBetRedirectionButtonContainer';
import MissionDetailsRuleListContainer from '../MissionDetailsRuleList/MissionDetailsRuleListContainer';
import MissionDetailsRulesButtonContainer from '../MissionDetailsRulesButton/MissionDetailsRulesButtonContainer';
import MissionDetailsSummaryContainer from '../MissionDetailsSummary/MissionDetailsSummaryContainer';

const APPEAR_TIMEOUT = 300; // ms

const Wrapper = styled.div`
  position: relative;

  height: 100%;
  width: 100%;

  background: black;
  color: white;
`;

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const WrapperContent = styled.div`
  height: 100%;
  width: 100%;

  &.mission-details-content-transitions-enter {
    animation: ${appearAnim} ${APPEAR_TIMEOUT}ms ease-out forwards;
  }

  &.mission-details-content-transitions-exit {
    animation: ${appearAnim} ${APPEAR_TIMEOUT}ms ease-out forwards reverse;
  }

  position: relative;
  margin: 0;
  padding: 1em 1em 1.5em;

  background: black;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledWinaLoader = styled(WinaLoader)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;

  &.mission-details-loader-transitions-enter {
    animation: ${appearAnim} ${APPEAR_TIMEOUT}ms ease-out forwards;
  }

  &.mission-details-loader-transitions-exit {
    animation: ${appearAnim} ${APPEAR_TIMEOUT}ms ease-out forwards reverse;
  }
`;

const Title = styled.div`
  font-size: 130%;
  font-weight: bold;
`;

const Description = styled.div`
  color: white;
  z-index: 1;
  font-size: 120%;
  letter-spacing: 0.03em;
  font-weight: 500;
  padding-top: 0;

  &:first-letter {
    text-transform: capitalize;
  }
`;

const SCROLLABLE_CONTENT_NEAR_TOP_CLASS_NAME = 'scroll-is-near-top';
const SCROLLABLE_CONTENT_NEAR_BOTTOM_CLASS_NAME = 'scroll-is-near-bottom';

const ScrollableContentWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: scroll;

  border-radius: 0.4em;

  height: intrinsic; /* Fixes Safari's getBoundingClientRect undefined issue */

  &.${SCROLLABLE_CONTENT_NEAR_TOP_CLASS_NAME} {
    &:before {
      opacity: 0;
    }
  }

  &.${SCROLLABLE_CONTENT_NEAR_BOTTOM_CLASS_NAME} {
    &:after {
      opacity: 0;
    }
  }

  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const ScrollableContent = styled.div`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  border-radius: inherit;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  max-height: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    margin-bottom: 0.67em;
  }
`;

const StyledMissionDetailsAchievementsListContainer = styled(MissionDetailsAchievementsListContainer)`
  > * {
    transform-style: flat;
  }
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
  padding-top: 1em;
  margin-bottom: 1.67em;
`;

const StyledMissionDetailsRulesButtonContainer = styled(MissionDetailsRulesButtonContainer)`
  margin-right: 1.33em;
  justify-content: flex-end;
  flex: 1;
  transform: translateZ(100px);
`;

const StyledMissionDetailsBetRedirectionButtonContainer = styled(MissionDetailsBetRedirectionButtonContainer)`
  padding: 0 0.75em 0 1em;
`;

type MissionDetailsProps = {
  className?: string;
  missionId: number;
  title?: string;
  description?: string;
  missionStatus: MissionCardComplexeStatus;
  operationType?: MissionOperationType;
  draggableHandler?: () => void;
  isUserConnected: boolean;
  optInRequired: boolean;
  hasUserRegistered: boolean;
  shouldTransition: boolean;
  promotionStatus: PromotionStatus;
  missionsModerator: boolean;
};

const MissionDetails = ({
  className = '',
  missionId,
  title,
  description,
  missionStatus,
  operationType,
  isUserConnected,
  optInRequired,
  hasUserRegistered,
  shouldTransition,
  promotionStatus,
  missionsModerator,
}: MissionDetailsProps) => {
  const isUserEnrollable = useMemo(
    () => !missionsModerator && promotionStatus === PromotionStatuses.DEFAULT,
    [missionsModerator, promotionStatus],
  );

  return (
    <Wrapper className={className}>
      <CSSTransition
        in={!!missionId}
        timeout={APPEAR_TIMEOUT}
        classNames="mission-details-content-transitions"
        mountOnEnter
        unmountOnExit
      >
        <WrapperContent>
          <ScrollableContentWrapper>
            <ScrollableContent>
              {title ? <Title>{title}</Title> : null}
              <MissionDetailsSummaryContainer missionId={missionId} missionStatus={missionStatus} />
              <Description>{description}</Description>
              <StyledMissionDetailsAchievementsListContainer missionId={missionId} />
              <MissionDetailsRuleListContainer missionId={missionId} />
            </ScrollableContent>
          </ScrollableContentWrapper>
          <BottomRow>
            <ButtonRow>
              <StyledMissionDetailsRulesButtonContainer missionId={missionId} />
              {missionStatus === MissionCardComplexeStatuses.RUNNING && isUserEnrollable ? (
                <StyledMissionDetailsBetRedirectionButtonContainer missionId={missionId} />
              ) : null}
            </ButtonRow>
            {isUserEnrollable ? (
              <MissionCardCTA
                missionId={missionId}
                missionStatus={missionStatus}
                operationType={operationType}
                isUserConnected={isUserConnected}
                optInRequired={optInRequired}
                hasUserRegistered={hasUserRegistered}
                shouldTransition={shouldTransition}
                large
              />
            ) : null}
          </BottomRow>
        </WrapperContent>
      </CSSTransition>
      <CSSTransition
        in={!missionId}
        timeout={APPEAR_TIMEOUT}
        classNames="mission-details-loader-transitions"
        mountOnEnter
        unmountOnExit
      >
        <StyledWinaLoader />
      </CSSTransition>
    </Wrapper>
  );
};

export default MissionDetails;
